/**
 * ==========================================
 * PUSHER MANAGER - Singleton Real-time Connection Manager
 * ==========================================
 * File: frontend/src/modules/session/services/pusher_manager.ts
 *
 * Centralized Pusher connection management following Pusher best practices.
 * Maintains a single Pusher instance across the app, handles authentication,
 * manages reconnection logic, and tracks all active subscriptions.
 */

import Pusher, { Channel, PresenceChannel } from 'pusher-js';
import type { PusherConfig, ConnectionStatus } from '../types/live_session';
import { apiClient } from '../../../core/config/api';

// ============================================
// TYPES
// ============================================

export type ConnectionStateCallback = (state: ConnectionStatus) => void;
export type ChannelEventCallback = (data: unknown) => void;

interface ChannelSubscription {
  channel: Channel | PresenceChannel;
  eventHandlers: Map<string, ChannelEventCallback[]>;
  refCount: number;
}

// ============================================
// PUSHER MANAGER CLASS
// ============================================

/**
 * Singleton class for managing Pusher connections.
 *
 * Usage:
 *   const manager = PusherManager.getInstance();
 *   await manager.initialize(config);
 *   const channel = manager.subscribe('private-live-session-123');
 */
class PusherManager {
  private static instance: PusherManager | null = null;
  private pusher: Pusher | null = null;
  private subscriptions: Map<string, ChannelSubscription> = new Map();
  private connectionStateCallbacks: Set<ConnectionStateCallback> = new Set();
  private initCallbacks: Set<() => void> = new Set();
  private currentState: ConnectionStatus = 'disconnected';
  private initialized: boolean = false;
  private authEndpoint: string = 'pusher/auth';
  private participantId: string | null = null;
  private config: PusherConfig | null = null;

  // Private constructor for singleton pattern
  private constructor() {}

  /**
   * Get the singleton instance of PusherManager
   */
  public static getInstance(): PusherManager {
    if (!PusherManager.instance) {
      PusherManager.instance = new PusherManager();
    }
    return PusherManager.instance;
  }

  /**
   * Initialize Pusher with configuration
   */
  public initialize(config: PusherConfig): boolean {
    if (this.initialized && this.pusher) {
      return true;
    }

    if (!config.configured || !config.key || !config.cluster) {
      console.warn('[PusherManager] Pusher not configured:', config.message);
      return false;
    }

    // Set auth endpoint if provided
    if (config.auth_endpoint) {
      this.authEndpoint = config.auth_endpoint;
    }

    // The client is constructed on first subscribe - pusher-js opens its
    // socket in the constructor, so building it here would connect every page.
    this.config = config;
    this.initialized = true;
    return true;
  }

  /**
   * Build the Pusher client on first use
   */
  private ensureClient(): Pusher | null {
    if (this.pusher) {
      return this.pusher;
    }

    const config = this.config;
    if (!config?.key || !config.cluster) {
      return null;
    }

    try {
      this.pusher = new Pusher(config.key, {
        cluster: config.cluster,
        authorizer: (channel) => ({
          authorize: async (socketId, callback) => {
            try {
              // Build headers
              const headers: Record<string, string> = {
                'Content-Type': 'application/x-www-form-urlencoded'
              };

              // Add participant ID header if available (for anonymous users)
              if (this.participantId) {
                headers['X-Participant-ID'] = this.participantId;
              }

              // Use apiClient which already has auth token configured
              const response = await apiClient.post(
                this.authEndpoint,
                new URLSearchParams({
                  socket_id: socketId,
                  channel_name: channel.name
                }),
                { headers }
              );
              callback(null, response.data);
            } catch (error: unknown) {
              const err = error as { response?: { status?: number }, message?: string };
              callback(
                new Error(err.response?.status?.toString() || err.message || 'Auth failed'),
                null
              );
            }
          }
        })
      });

      // Set up connection state monitoring
      this.setupConnectionStateHandlers();

      return this.pusher;
    } catch (error) {
      console.error('[PusherManager] Failed to create client:', error);
      this.pusher = null;
      return null;
    }
  }


  /**
   * Set participant ID for anonymous users
   * This should be called after join to enable Pusher authentication
   */
  public setParticipantId(participantId: string | null): void {
    this.participantId = participantId;
  }

  /**
   * Get current participant ID
   */
  public getParticipantId(): string | null {
    return this.participantId;
  }

  /**
   * Set up connection state change handlers
   */
  private setupConnectionStateHandlers(): void {
    if (!this.pusher) return;

    this.pusher.connection.bind('state_change', (states: { previous: string; current: string }) => {
      const newState = this.mapPusherState(states.current);
      this.currentState = newState;

      // Notify all listeners
      this.connectionStateCallbacks.forEach(callback => {
        try {
          callback(newState);
        } catch (error) {
          console.error('[PusherManager] Error in connection state callback:', error);
        }
      });
    });

    this.pusher.connection.bind('error', (error: Error) => {
      console.error('[PusherManager] Connection error:', error);
    });
  }

  /**
   * Map Pusher connection state to our ConnectionStatus type
   */
  private mapPusherState(pusherState: string): ConnectionStatus {
    switch (pusherState) {
      case 'connected':
        return 'connected';
      case 'connecting':
      case 'unavailable':
        return 'reconnecting';
      default:
        return 'disconnected';
    }
  }

  /**
   * Subscribe to a channel
   */
  public subscribe(channelName: string): Channel | PresenceChannel | null {
    // Return existing subscription if already subscribed
    const existing = this.subscriptions.get(channelName);
    if (existing) {
      existing.refCount += 1;
      return existing.channel;
    }

    const pusher = this.ensureClient();
    if (!pusher) {
      console.warn('[PusherManager] Cannot subscribe - not initialized');
      return null;
    }

    try {
      const state = pusher.connection.state;
      if (state !== 'connected' && state !== 'connecting') {
        pusher.connect();
      }

      let channel: Channel | PresenceChannel;

      if (channelName.startsWith('presence-')) {
        channel = pusher.subscribe(channelName) as PresenceChannel;
      } else {
        channel = pusher.subscribe(channelName);
      }

      this.subscriptions.set(channelName, {
        channel,
        eventHandlers: new Map(),
        refCount: 1
      });
      return channel;
    } catch (error) {
      console.error(`[PusherManager] Failed to subscribe to ${channelName}:`, error);
      return null;
    }
  }

  /**
   * Unsubscribe from a channel
   */
  public unsubscribe(channelName: string): void {
    if (!this.pusher) return;

    const subscription = this.subscriptions.get(channelName);
    if (!subscription) return;

    subscription.refCount -= 1;
    if (subscription.refCount > 0) {
      return;
    }

    // Unbind all event handlers
    subscription.eventHandlers.forEach((handlers, event) => {
      handlers.forEach(handler => {
        subscription.channel.unbind(event, handler);
      });
    });

    this.pusher.unsubscribe(channelName);
    this.subscriptions.delete(channelName);

    if (this.subscriptions.size === 0) {
      this.pusher.disconnect();
      this.pusher = null;
      this.currentState = 'disconnected';
    }
  }

  /**
   * Bind an event handler to a channel
   */
  public bind(
    channelName: string,
    event: string,
    callback: ChannelEventCallback
  ): boolean {
    const subscription = this.subscriptions.get(channelName);
    if (!subscription) {
      console.warn(`[PusherManager] Channel ${channelName} not subscribed`);
      return false;
    }

    // Track the handler
    if (!subscription.eventHandlers.has(event)) {
      subscription.eventHandlers.set(event, []);
    }
    subscription.eventHandlers.get(event)!.push(callback);

    // Bind to channel
    subscription.channel.bind(event, callback);
    return true;
  }

  /**
   * Unbind an event handler from a channel
   */
  public unbind(
    channelName: string,
    event: string,
    callback?: ChannelEventCallback
  ): void {
    const subscription = this.subscriptions.get(channelName);
    if (!subscription) return;

    if (callback) {
      subscription.channel.unbind(event, callback);

      // Remove from tracked handlers
      const handlers = subscription.eventHandlers.get(event);
      if (handlers) {
        const index = handlers.indexOf(callback);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    } else {
      // Unbind all handlers for this event
      subscription.channel.unbind(event);
      subscription.eventHandlers.delete(event);
    }
  }

  /**
   * Get current connection state
   */
  public getConnectionState(): ConnectionStatus {
    // If pusher exists, get the actual current state from the connection
    // This ensures we return the correct state even if we missed a state change event
    if (this.pusher && this.pusher.connection) {
      const actualState = this.mapPusherState(this.pusher.connection.state);
      // Update cached state if it's different
      if (actualState !== this.currentState) {
        this.currentState = actualState;
      }
      return actualState;
    }
    return this.currentState;
  }

  /**
   * Register a connection state change callback
   */
  public onConnectionStateChange(callback: ConnectionStateCallback): () => void {
    this.connectionStateCallbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.connectionStateCallbacks.delete(callback);
    };
  }

  /**
   * Check if manager is initialized and connected
   */
  public isConnected(): boolean {
    return this.initialized && this.currentState === 'connected';
  }

  /**
   * Check if manager is initialized
   */
  public isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get a subscribed channel
   */
  public getChannel(channelName: string): Channel | PresenceChannel | null {
    return this.subscriptions.get(channelName)?.channel ?? null;
  }

  /**
   * Disconnect and clean up
   */
  public disconnect(): void {
    // Force teardown regardless of refcounts
    this.subscriptions.forEach((subscription, channelName) => {
      subscription.eventHandlers.forEach((handlers, event) => {
        handlers.forEach(handler => subscription.channel.unbind(event, handler));
      });
      this.pusher?.unsubscribe(channelName);
    });
    this.subscriptions.clear();

    this.pusher?.disconnect();
    this.pusher = null;
    this.initialized = false;
    this.config = null;
    this.currentState = 'disconnected';
  }

  /**
   * Reconnect Pusher
   */
  public reconnect(): void {
    if (this.pusher) {
      this.pusher.connect();
    }
  }
}

// Export singleton getter
export const getPusherManager = (): PusherManager => PusherManager.getInstance();

export default PusherManager;
